import React, {
  useState,
  useMemo,
  useCallback,
  ReactElement,
  useEffect,
  useRef,
} from 'react';
import { Flex, Text } from 'rebass/styled-components';
import { Row } from 'react-table';

import useFormsColumns from 'containers/FormsList/useFormsColumns';

import { Button, Stack } from 'ui/components';
import { Table, SearchInput } from 'ui/patterns';

import { useGetFormsList } from 'hooks';

import * as Icon from 'assets/icons';

import { Forms } from 'types/forms';
import { SortItemType } from 'types/modalTypes';
import { FormStateProps } from './copySection';

const formsSortItem = [
  { sortKey: 'Form Notes', sortOrder: 'desc' },
  { sortKey: 'Form Status', sortOrder: 'desc' },
];

const columnSearchInitial = {
  'Form Status': 'Enabled',
};

function useFormsState({ accountId, showPanel }: FormStateProps) {
  const tbodyRef = useRef() as React.MutableRefObject<HTMLTableSectionElement>;

  const columns = useFormsColumns(['Form Notes', 'Form Status'], true);
  const [keywordSearch, setKeywordSearch] = useState(''); // keyword search
  const [columnsSearch, setColumnsSearch] = useState<Record<string, string>>({
    ...columnSearchInitial,
  }); // header search
  const [currentSortItem, setCurrentSortItem] = useState<
    SortItemType | Array<SortItemType>
  >([...formsSortItem]);

  const {
    data: formList,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
  } = useGetFormsList({
    accountId,
    keywordSearch,
    columnsSearch,
    sortItem: currentSortItem,
  });

  const headerUpdateCallBack = useCallback(
    ({ columnId, value }: { columnId: string; value: string }) => {
      setColumnsSearch({
        ...columnsSearch,
        [columnId]: value,
      });
    },
    [columnsSearch, setColumnsSearch],
  );

  const handleSortChange = useCallback(
    (item: string): void => {
      let sortOrder = 'asc';
      if (currentSortItem instanceof Array) {
        const existingItem = currentSortItem.find(
          sItem => sItem.sortKey === item,
        );
        if (existingItem) {
          setCurrentSortItem({
            sortKey: item,
            sortOrder: existingItem.sortOrder === 'asc' ? 'desc' : 'asc',
          });
        } else {
          setCurrentSortItem({
            sortKey: item,
            sortOrder,
          });
        }
      } else if (currentSortItem.sortKey && currentSortItem.sortOrder) {
        if (
          currentSortItem.sortKey === item &&
          currentSortItem.sortOrder === 'asc'
        ) {
          sortOrder = 'desc';
        }
        setCurrentSortItem({
          sortKey: item,
          sortOrder,
        });
      }
    },
    [setCurrentSortItem, currentSortItem],
  );

  const handleKeywordSearch = (value: string): void => {
    setKeywordSearch(value);
  };

  const handleReset = (): void => {
    setKeywordSearch('');
    setColumnsSearch({ ...columnSearchInitial });
    setCurrentSortItem([...formsSortItem]);
  };

  const formTableData = useMemo(() => {
    if (formList) {
      return formList.pages.map((dataItem: any) => dataItem?.Data).flat();
    }
  }, [formList]);

  const totalFormsItems = useMemo(() => {
    if (formList) {
      return formList?.pages[0]?.FilteredRecords;
    }
    return 0;
  }, [formList]);

  const handleScroll = useCallback(
    (e: any) => {
      if (tbodyRef.current === e.target) {
        const bottom =
          e.target.clientHeight - 10 <
            e.target.scrollHeight - e.target.scrollTop &&
          e.target.scrollHeight - e.target.scrollTop <
            e.target.clientHeight + 10;
        if (bottom) {
          hasNextPage && fetchNextPage();
        }
      }
    },
    [fetchNextPage, hasNextPage],
  );

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [handleScroll]);

  const onFormsEndReached = () => {
    if (formTableData) {
      if (hasNextPage && formTableData?.length < totalFormsItems)
        fetchNextPage();
    }
  };

  useEffect(() => {
    handleReset();
  }, [showPanel]);

  return {
    totalFormsItems,
    isFetching,
    handleReset,
    handleKeywordSearch,
    keywordSearch,
    formTableProps: {
      columns,
      data: formTableData,
      isLoading: isLoading || isFetching,
      headerUpdateCallBack,
      handleSortChange,
      columnsSearch,
      multiSortedItem: currentSortItem,
      hasInlineLoader: true,
      onEndReached: onFormsEndReached,
      isFetching: !!isFetchingNextPage,
    },
  };
}

export default function FormPanel({
  selectedAccountId,
  selectedFormId,
  setSelectedFromId,
  isHidden,
  showPanel,
}: {
  selectedAccountId: number;
  selectedFormId: number;
  setSelectedFromId: (id: number) => void;
  isHidden: boolean;
  showPanel: boolean;
}): ReactElement {
  const {
    totalFormsItems,
    isFetching,
    handleReset,
    handleKeywordSearch,
    keywordSearch,
    formTableProps,
  } = useFormsState({ accountId: selectedAccountId, showPanel: showPanel });
  const onFormRowClick = (row: Row<Forms>) => {
    setSelectedFromId(row?.original?.Id);
  };
  if (isHidden) return <></>;
  return (
    <>
      <Flex justifyContent="space-between" alignItems="center" mb={2}>
        <Text fontSize={1} color="text.body">
          {formTableProps.data && formTableProps.data.length > 0 && (
            <Text mb={2}>{`Showing ${
              formTableProps.data.length
            } of ${totalFormsItems}${isFetching ? ' | Processing' : ''}`}</Text>
          )}
        </Text>
        <Stack direction="x" gap={2}>
          <SearchInput
            handleChange={handleKeywordSearch}
            value={keywordSearch}
            placeholder="Search Forms"
          />
          <Button
            onClick={handleReset}
            iconBefore={<Icon.Refresh title="" ariaHidden="true" />}
            variant="secondary"
          >
            Reset All
          </Button>
        </Stack>
      </Flex>

      <Table
        {...formTableProps}
        height="450px"
        onRowClick={onFormRowClick}
        selectedRowId={selectedFormId}
        noOptions
      />
    </>
  );
}
